<template>
  <div class="ticket-mail-view" :class="{ compact }">
    <div v-if="!compact" class="ticket-mail-header">
      <div class="ticket-mail-subject">{{ mail.subject || '(senza oggetto)' }}</div>
      <div class="ticket-mail-meta">
        <div><span class="lbl">Da</span> {{ fromLabel }}</div>
        <div v-if="toLabel"><span class="lbl">A</span> {{ toLabel }}</div>
        <div v-if="ccLabel"><span class="lbl">Cc</span> {{ ccLabel }}</div>
        <div v-if="mail.date"><span class="lbl">Data</span> {{ formatDate(mail.date) }}</div>
      </div>
    </div>

    <div v-if="loading" class="ticket-mail-loading">Caricamento email…</div>

    <iframe
      v-show="!loading"
      ref="frame"
      class="ticket-mail-frame"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      title="Anteprima email"
    ></iframe>
  </div>
</template>

<script>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import moment from 'moment'

export default {
  name: 'TicketMailView',
  props: {
    mail: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const frame = ref(null)
    const loading = ref(true)
    const objectUrls = []
    const ticketChat = inject('ticketChat', null)
    const fetchAttachmentBlob = ticketChat?.fetchAttachmentBlob

    const formatAddresses = (list) => {
      if (!Array.isArray(list) || list.length === 0) return ''
      return list
        .map((item) => {
          if (!item) return ''
          if (typeof item === 'string') return item
          const email = item.email || item.address || ''
          const name = item.name || ''
          if (name && email) return `${name} <${email}>`
          return name || email
        })
        .filter(Boolean)
        .join(', ')
    }

    const fromLabel = computed(() => {
      const name = props.mail?.from_name
      const email = props.mail?.from_email
      if (name && email) return `${name} <${email}>`
      return name || email || '—'
    })

    const toLabel = computed(() => formatAddresses(props.mail?.to_addresses))
    const ccLabel = computed(() => formatAddresses(props.mail?.cc_addresses))

    const formatDate = (value) => {
      const m = moment(value)
      return m.isValid() ? m.locale('it').format('DD MMM YYYY HH:mm') : value
    }

    const escapeHtml = (text) =>
      String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

    const decodeBasicEntities = (html) =>
      String(html || '')
        .replace(/&amp;nbsp;/gi, '&nbsp;')
        .replace(/&amp;lt;/gi, '&lt;')
        .replace(/&amp;gt;/gi, '&gt;')
        .replace(/&amp;quot;/gi, '&quot;')
        .replace(/&amp;amp;/gi, '&amp;')

    const revokeUrls = () => {
      while (objectUrls.length) {
        URL.revokeObjectURL(objectUrls.pop())
      }
    }

    const resolveCidHtml = async (html) => {
      let out = html || ''
      const cidList = props.mail?.cid_attachments || []
      if (!out || !cidList.length || typeof fetchAttachmentBlob !== 'function') {
        return out
      }

      for (const att of cidList) {
        const rawCid = String(att.content_id || '').replace(/^<|>$/g, '')
        if (!rawCid) continue
        try {
          const blob = await fetchAttachmentBlob(att)
          if (!blob) continue
          const url = URL.createObjectURL(blob)
          objectUrls.push(url)
          const escaped = rawCid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          out = out.replace(new RegExp(`cid:${escaped}`, 'gi'), url)
        } catch (err) {
          console.warn('CID allegato non risolto', att?.mail_attachment_id, err)
        }
      }
      return out
    }

    const buildSrcDoc = (bodyHtml) => {
      const cleaned = decodeBasicEntities(bodyHtml)
      if (cleaned && String(cleaned).trim() !== '') {
        return cleaned
      }
      const text = escapeHtml(props.mail?.body_text || '')
      return `<pre style="white-space:pre-wrap;font-family:sans-serif;font-size:14px;margin:0">${text || '(nessun contenuto)'}</pre>`
    }

    const paintFrame = async () => {
      loading.value = true
      revokeUrls()
      const resolved = await resolveCidHtml(props.mail?.body_html || '')
      const html = buildSrcDoc(resolved)
      await nextTick()
      const iframe = frame.value
      if (!iframe) {
        loading.value = false
        return
      }

      const onLoad = () => {
        try {
          const doc = iframe.contentDocument
          if (!doc) return
          const style = doc.createElement('style')
          style.textContent = `
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #374151;
              margin: 0;
              padding: ${props.compact ? '8px 4px' : '12px 14px'};
              word-break: break-word;
              background: #fff;
            }
            img { max-width: 100% !important; height: auto !important; }
            table { max-width: 100% !important; }
            pre, code { white-space: pre-wrap; word-break: break-all; }
            blockquote {
              margin: 8px 0;
              padding-left: 12px;
              border-left: 3px solid #93c5fd;
              color: #6b7280;
            }
          `
          doc.head.appendChild(style)
          doc.querySelectorAll('a').forEach((a) => {
            a.setAttribute('target', '_blank')
            a.setAttribute('rel', 'noopener noreferrer')
          })
          iframe.style.height = `${Math.max(doc.body.scrollHeight + 20, 80)}px`
        } catch (e) {
          // ignore
        } finally {
          loading.value = false
        }
      }

      iframe.removeEventListener('load', onLoad)
      iframe.addEventListener('load', onLoad)
      iframe.srcdoc = html
    }

    onMounted(paintFrame)
    watch(() => [props.mail?.id, props.compact], paintFrame)
    onBeforeUnmount(revokeUrls)

    return {
      frame,
      loading,
      fromLabel,
      toLabel,
      ccLabel,
      formatDate,
    }
  },
}
</script>

<style scoped>
.ticket-mail-view {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-top: 4px;
}

.ticket-mail-view.compact {
  border: 0;
  border-radius: 0;
  margin-top: 0;
}

.ticket-mail-header {
  padding: 10px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.ticket-mail-subject {
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  margin-bottom: 6px;
}

.ticket-mail-meta {
  font-size: 12px;
  color: #4b5563;
  display: grid;
  gap: 2px;
}

.ticket-mail-meta .lbl {
  display: inline-block;
  min-width: 36px;
  color: #9ca3af;
  font-weight: 600;
  margin-right: 6px;
}

.ticket-mail-loading {
  padding: 16px;
  font-size: 12px;
  color: #6b7280;
}

.ticket-mail-frame {
  width: 100%;
  border: 0;
  display: block;
  min-height: 80px;
  background: #fff;
}
</style>
